import { useEffect } from 'react';
import { debounce } from 'lodash';
import { type MessagePayload } from 'firebase/messaging';
import { useQueryClient } from 'react-query';
import { onMessage, getMessaging } from '@firebase/messaging';
import {useNavigate} from 'react-router-dom';

import {
    type IGetConversationsResponse,
    Auth,
    apiClient,
    useSubscribeToPushNotifications,
    useLoggedInUser,
    useAchievements,
} from 'src/services/api';
import { queryKeys } from 'src/services/api/constants';
import { shouldShowNotification } from 'src/services/push-notifications/should-show-notification';
import {
    useUpdateConversations,
} from 'src/services/push-notifications/use-update-conversations';
import { isAuthError } from 'src/services/api/auth/auth-errors';
import { saveNotificationSetting, clearNotificationSetting } from 'src/services/push-notifications/sw-store';
import { fbApp, getPushToken } from 'src/services/push-notifications/firebase';
import {getPermission} from 'src/services/push-notifications/utils';
import { wait } from 'src/utils/wait';
import { normalizePushPayloadData } from 'src/services/push-notifications/payload';
import { getIsLive } from 'src/services/websocket/live-updates-signal';
import { analyticsClient, AnalyticsEvent } from 'src/services/analytics';

import reffieLogosBrainTransparent32 from 'src/assets/img/reffie-logos/brain-transparent-32.png';

enum PushNotificationType {
    NEW_LEAD_MESSAGE = 'NEW_LEAD_MESSAGE',
    NEW_ACHIEVEMENT_EARNED = 'NEW_ACHIEVEMENT_EARNED',
    TEAM_AVAILABILITY_CHANGED = 'TEAM_AVAILABILITY_CHANGED'
}


export const usePushNotifications = () => {
    const updateConversations = useUpdateConversations();
    const { data: loggedInUser, refetch: refetchUserSettings } = useLoggedInUser();
    const userSettings = loggedInUser?.settings;
    const { refetch: refetchAchievements } = useAchievements();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const setting = userSettings?.notifications?.new_message;
    useEffect(() => {
        if (setting) {
            saveNotificationSetting(setting);
        } else {
            clearNotificationSetting();
        }
    }, [setting]);

    useEffect(() => {
        const handleLogout = () => clearNotificationSetting();
        const handleLogin = () => { refetchUserSettings(); };
        document.addEventListener('reffie_logout', handleLogout);
        document.addEventListener('reffie_login', handleLogin);
        return () => {
            document.removeEventListener('reffie_logout', handleLogout);
            document.removeEventListener('reffie_login', handleLogin);
        };
    }, [refetchUserSettings]);


    useEffect(() => {
        if (!navigator || !navigator.serviceWorker) {
            return;
        }

        const messaging = getMessaging(fbApp);

        const handleAchievementMessage = async () => {
            // Only show confetti if achievements are enabled
            if (userSettings?.is_achievements_enabled !== false) {
                await wait(2000);
                await refetchAchievements();
            }
        };

        const handleMessage = debounce(async (payload: MessagePayload) => {
            const data = normalizePushPayloadData(payload);

            const notificationTitle = data.title || 'New message from lead';
            const notificationBody = data.body || 'You have a new message from lead.';
            const leadUuid = data.lead_uuid;

            if (shouldShowNotification(userSettings, leadUuid, queryClient)) {
                const notification = new Notification(notificationTitle, { body: notificationBody, icon: reffieLogosBrainTransparent32 });

                notification.onclick = () => {
                    window?.focus();
                    if (leadUuid) {
                        analyticsClient.track(AnalyticsEvent.CONVERSATION_OPENED, {
                            source: 'push_notification',
                            variant: 'page',
                            lead_uuid: leadUuid,
                        });
                        navigate(`/landlord/inner/messages/from-lead?lead_uuid=${leadUuid}`);
                    } else {
                        navigate('/landlord/inner/root');
                    }
                    notification.close();
                };
            }
            if (!getIsLive()) {
                const searchParams = new URLSearchParams();
                const since = new Date();
                since.setMinutes(since.getMinutes() - 5);
                searchParams.append('since', since.toISOString().slice(0, -1));
                // constrain to the active working set; without this the backend drops the
                // archival filter and aggregates over every lead in the team (major RDS load)
                searchParams.append('is_archived', 'false');
                try {
                    const res = await apiClient('api/landlord/conversations', {
                        searchParams,
                    }).json<IGetConversationsResponse>();

                    updateConversations(res.conversations);
                } catch (error: any) {
                    // Same treatment as every other rejected request: end the
                    // session and redirect, rather than clearing the access
                    // token alone and leaving a dead refresh token behind.
                    if (isAuthError(error)) {
                        Auth.redirectToLoginPage();
                    } else {
                        console.error('Failed to fetch conversations:', error);
                    }
                }
            }
        }, 2000, { leading: true, trailing: true });
        return onMessage(messaging, async (payload) => {
            const data = normalizePushPayloadData(payload);
            const notificationType = data.type || {};

            if (notificationType === PushNotificationType.NEW_LEAD_MESSAGE) {
                await handleMessage(payload);
            } else if (notificationType === PushNotificationType.NEW_ACHIEVEMENT_EARNED) {
                await handleAchievementMessage();
            } else if (notificationType === PushNotificationType.TEAM_AVAILABILITY_CHANGED) {
                // a teammate changed presence -> refresh the roster and our own presence
                queryClient.invalidateQueries([queryKeys.teamMembers]);
                queryClient.invalidateQueries([queryKeys.loggedInUser]);
            }
        });
    }, [updateConversations, userSettings, queryClient, navigate]);

    const { mutate: subscribeToPushNotifications } = useSubscribeToPushNotifications();

    useEffect(() => {
        const subscribe = async () => {
            try {
                const permission = await getPermission();
                if (permission !== 'granted') {
                    console.log('Push notifications permission not granted:', permission);
                    return;
                }
                const token = await getPushToken();
                if (token) {
                    subscribeToPushNotifications({
                        token
                    });
                }
            } catch (error) {
                console.error('Error creating push subscription:', error);
                throw error;
            }
        };

        subscribe();
    }, []);
};

export const usePushSubscription = () => {
    const { mutateAsync: subscribeToPushNotifications } = useSubscribeToPushNotifications();

    const createPushSubscription = async (): Promise<boolean> => {
        try {
            const token = await getPushToken();
            if (token) {
                await subscribeToPushNotifications({ token });
                return true;
            }
            return false;
        } catch(error) {
            console.error('usePushSubscription error', error);
            return false;
        }
    };

    return { createPushSubscription };
};
